import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { ArrowLeftIconMedium } from '@hm-group/fabric-icons/icons/ArrowLeftIcon';
import { ArrowRightIconMedium } from '@hm-group/fabric-icons/icons/ArrowRightIcon';
import getCurrentBreakpoint from '@hm-group/frontend-utils/breakpoints/getCurrentBreakpoint/getCurrentBreakpoint';
import cn from 'classnames';
import IconButton from "../Buttons/IconButton";
import "./CarouselOverflow.css";
var styles = { "wrapper": "a45205", "arrow": "ced1b5", "arrowLeft": "fe33f6", "arrowRight": "cff651", "track": "d36894", "slide": "b71252", "slide_xsmall": "f9f931", "slide_none": "a69978", "arrowPrev": "ea52ec", "arrowNext": "ba6481" };
const CarouselOverflow = forwardRef(({ slideSpacing = 'xsmall', children, initialSlide, labels, slidesPerBreakpoint = {
    default: 1,
}, offset = 'default', onInView, onNextClick, onPreviousClick, testId, }, ref) => {
    const [isDisabledPrev, setIsDisabledPrev] = useState(true);
    const [isDisabledNext, setIsDisabledNext] = useState(false);
    const [shouldShowArrows, setShouldShowArrows] = useState(true);
    const [inViewIndexes, setInViewIndexes] = useState([]);
    const [slideWidth, setSlideWidth] = useState('auto');
    const trackRef = useRef(null);
    const wrapperRef = useRef(null);
    const listItemRefs = useRef([]);
    useImperativeHandle(ref, () => wrapperRef.current);
    useEffect(() => {
        if (onInView) {
            onInView(inViewIndexes);
        }
    }, [inViewIndexes, onInView]);
    useEffect(() => {
        // calculate each slide's width based on current active breakpoint
        const updateBreakpoint = () => {
            if (wrapperRef.current) {
                const breakpoint = getCurrentBreakpoint();
                const numOfSlides = slidesPerBreakpoint[breakpoint] || slidesPerBreakpoint.default;
                const wrapperWidth = wrapperRef.current.clientWidth;
                const xsmallSpacing = 2;
                let negativeSpacingPerSet = slideSpacing === 'xsmall' ? xsmallSpacing * (numOfSlides - 1) : 0;
                let numOfSlidesWithOffset = numOfSlides;
                if (offset) {
                    negativeSpacingPerSet += slideSpacing === 'xsmall' ? xsmallSpacing : 0;
                    if (offset === 'default') {
                        // calculate what percentage is 32px of the slide's width
                        const widthWithoutOffset = (wrapperWidth - negativeSpacingPerSet) / numOfSlides;
                        numOfSlidesWithOffset += 32 / widthWithoutOffset;
                    }
                    else {
                        numOfSlidesWithOffset += 0.5;
                    }
                }
                const width = (wrapperWidth - negativeSpacingPerSet) / numOfSlidesWithOffset;
                setSlideWidth(`${width}px`);
                setShouldShowArrows(React.Children.toArray(children).length > numOfSlides);
            }
        };
        updateBreakpoint();
        // register event listener on each state update to get the fresh state from within the listener's scope
        window.addEventListener('resize', updateBreakpoint);
        return () => {
            window.removeEventListener('resize', updateBreakpoint);
        };
    }, [slidesPerBreakpoint, children, offset, slideSpacing]);
    useEffect(() => {
        const onScroll = () => {
            // Disabled buttons logic
            if (wrapperRef.current && trackRef.current) {
                const flawMargin = 5;
                const wrapperWidth = wrapperRef.current.clientWidth;
                const { scrollLeft, scrollWidth } = trackRef.current;
                if (scrollLeft <= flawMargin && !isDisabledPrev) {
                    setIsDisabledPrev(true);
                }
                if (scrollLeft > flawMargin && isDisabledPrev) {
                    setIsDisabledPrev(false);
                }
                if (wrapperWidth + scrollLeft + flawMargin >= scrollWidth) {
                    setIsDisabledNext(true);
                }
                if (wrapperWidth + scrollLeft + flawMargin < scrollWidth) {
                    setIsDisabledNext(false);
                }
            }
            // In view logic
            if (trackRef.current && wrapperRef.current) {
                const { scrollLeft } = trackRef.current;
                const wrapperWidth = wrapperRef.current.clientWidth;
                const newArray = [];
                const itemWidth = parseInt(slideWidth, 10);
                const percentAsInView = 0.2;
                let isChanged = false;
                listItemRefs.current.forEach((item, itemIndex) => {
                    if (item &&
                        typeof item.offsetLeft === 'number' &&
                        // Item's left side is in view
                        item.offsetLeft < scrollLeft + wrapperWidth - percentAsInView * itemWidth &&
                        // Item's right side is in view
                        item.offsetLeft + itemWidth - percentAsInView * itemWidth > scrollLeft) {
                        newArray.push(itemIndex);
                        if (!inViewIndexes.includes(itemIndex)) {
                            isChanged = true;
                        }
                    }
                });
                if (isChanged || newArray.length !== inViewIndexes.length) {
                    setInViewIndexes(newArray);
                }
            }
        };
        onScroll();
        const reference = trackRef.current;
        if (reference) {
            reference.addEventListener('scroll', onScroll);
        }
        return () => {
            reference === null || reference === void 0 ? void 0 : reference.removeEventListener('scroll', onScroll);
        };
    }, [isDisabledPrev, isDisabledNext, inViewIndexes, slideWidth]);
    useEffect(() => {
        const onLoad = () => {
            // handle initialSlide behaviour
            if (trackRef.current && initialSlide) {
                const { scrollLeft } = trackRef.current;
                let scrollDiff = 0;
                let element = null;
                listItemRefs.current.forEach((item, index) => {
                    if (index === initialSlide) {
                        const diff = item.offsetLeft - scrollLeft;
                        if (scrollDiff === 0 || diff < scrollDiff) {
                            scrollDiff = diff;
                            element = item;
                        }
                    }
                });
                if (element && trackRef.current.scrollTo) {
                    trackRef.current.scrollTo({
                        left: element.offsetLeft,
                        behavior: 'smooth',
                    });
                }
            }
        };
        onLoad();
    }, [initialSlide]);
    const handlePreviousClick = () => {
        if (trackRef.current) {
            const { scrollLeft } = trackRef.current;
            let scrollDiff = 0;
            let element = null;
            let elementIndex = 0;
            listItemRefs.current.forEach((item, index) => {
                if ((item === null || item === void 0 ? void 0 : item.offsetLeft) < scrollLeft) {
                    const diff = scrollLeft - item.offsetLeft;
                    if (scrollDiff === 0 || diff < scrollDiff) {
                        scrollDiff = diff;
                        element = item;
                        elementIndex = index;
                    }
                }
            });
            if (element) {
                trackRef.current.scrollTo({
                    left: element.offsetLeft,
                    behavior: 'smooth',
                });
            }
            if (onPreviousClick) {
                onPreviousClick(elementIndex);
            }
        }
    };
    const handleNextClick = () => {
        if (trackRef.current) {
            const { scrollLeft } = trackRef.current;
            let scrollDiff = 0;
            let element = null;
            let elementIndex = 0;
            listItemRefs.current.forEach((item, index) => {
                if ((item === null || item === void 0 ? void 0 : item.offsetLeft) > scrollLeft) {
                    const diff = item.offsetLeft - scrollLeft;
                    if (scrollDiff === 0 || diff < scrollDiff) {
                        scrollDiff = diff;
                        element = item;
                        elementIndex = index;
                    }
                }
            });
            if (element) {
                trackRef.current.scrollTo({
                    left: element.offsetLeft,
                    behavior: 'smooth',
                });
            }
            if (onNextClick) {
                onNextClick(elementIndex);
            }
        }
    };
    return (React.createElement("div", { role: "presentation", className: styles.wrapper, ref: wrapperRef, "data-testid": testId },
        React.createElement("ul", { className: styles.track, ref: trackRef }, React.Children.toArray(children).map((child, index) => (React.createElement("li", { key: child.key, className: cn(styles.slide, styles[`slide_${slideSpacing}`]), style: { minWidth: slideWidth, width: slideWidth }, ref: (listItemRef) => {
                listItemRefs.current[index] = listItemRef;
            } }, child)))),
        shouldShowArrows && (React.createElement(React.Fragment, null,
            React.createElement(IconButton, { variant: "primary", borderRadius: "round", isReverse: true, isDisabled: isDisabledPrev, className: cn(styles.arrow, styles.arrowPrev), iconElement: React.createElement(ArrowLeftIconMedium, null), onClick: handlePreviousClick }, labels.prev),
            React.createElement(IconButton, { variant: "primary", borderRadius: "round", isReverse: true, isDisabled: isDisabledNext, className: cn(styles.arrow, styles.arrowNext), iconElement: React.createElement(ArrowRightIconMedium, null), onClick: handleNextClick }, labels.next)))));
});
export default CarouselOverflow;
//# sourceMappingURL=CarouselOverflow.js.map